import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Target, TrendUp, Warning, Lock, FloppyDisk, Trash, ArrowRight, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const fmt$ = (n) => (n == null ? "—" : `$${Math.round(n).toLocaleString()}`);

const LS_KEY = "optimizer.draft";
const lsLoad = () => {
  try { return JSON.parse(window.localStorage.getItem(LS_KEY) || "null"); } catch { return null; }
};
const lsSave = (v) => {
  try { window.localStorage.setItem(LS_KEY, JSON.stringify(v)); } catch { /* noop */ }
};

export default function StakeOptimizer() {
  const { user } = useAuth();
  const isLoggedIn = !!user;
  const isSubscriber = !!user && (user.role === "admin" || ["trial", "active", "trialing"].includes(user.subscription_status));

  // Inputs (persist draft so anon visitors don't lose state on reload).
  const initial = lsLoad() || { target_weekly: 500, bankroll: 5000 };
  const [target, setTarget] = useState(initial.target_weekly);
  const [bankroll, setBankroll] = useState(initial.bankroll);
  const [savedLadder, setSavedLadder] = useState(null);
  const [busy, setBusy] = useState(false);

  // Load any persisted ladder for logged-in users
  useEffect(() => {
    if (!isLoggedIn) return;
    api.get("/account/stake-ladder")
      .then(({ data }) => { if (data?.ladder) setSavedLadder(data.ladder); })
      .catch(() => { /* silent */ });
  }, [isLoggedIn]);

  // Persist draft on every change.
  useEffect(() => { lsSave({ target_weekly: target, bankroll }); }, [target, bankroll]);

  // Compute ladder client-side for instant preview. Server recomputes on save.
  const ladder = useMemo(() => {
    const t = Number(target);
    const b = bankroll ? Number(bankroll) : null;
    if (!t || t <= 0) return null;
    const factor = t / 1200;
    const roundTo5 = (v) => Math.max(5, Math.round(v / 5) * 5);
    const base = roundTo5(250 * factor);
    const tier2 = roundTo5(150 * factor);
    const tier3 = roundTo5(75 * factor);
    const warnings = [];
    if (b && base > b * 0.05) warnings.push(`Base unit $${base} is ${((base / b) * 100).toFixed(1)}% of your bankroll — above the 5% safety ceiling.`);
    if (b && base > b * 0.10) warnings.push("HIGH RISK · one losing week could materially deplete your bankroll.");
    return {
      target_weekly: t,
      bankroll: b,
      base_unit: base,
      tier2_unit: tier2,
      tier3_unit: tier3,
      tier2_at: t * 0.667,
      tier3_at: t * 1.0,
      warnings,
    };
  }, [target, bankroll]);

  const save = async () => {
    if (!isLoggedIn) {
      toast("Sign in to save your stake ladder", {
        description: "Subscribers also get every pick card on /sports/mlb automatically restamped with their personalized stake.",
        action: { label: "Subscribe", onClick: () => { window.location.href = "/pricing"; } },
      });
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.put("/account/stake-ladder", { target_weekly: target, bankroll: bankroll || null });
      setSavedLadder(data.ladder);
      toast.success("Stake ladder saved", { description: `Base $${data.ladder.base_unit} · Tier 2 $${data.ladder.tier2_unit} · Tier 3 $${data.ladder.tier3_unit}` });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!isLoggedIn || !savedLadder) return;
    setBusy(true);
    try {
      await api.delete("/account/stake-ladder");
      setSavedLadder(null);
      toast.success("Ladder cleared. Pick cards revert to the default $250.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[calc(100vh-180px)] py-10 px-6" data-testid="stake-optimizer-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#22C55E] mb-2">
            <Target size={12} weight="fill" /> Position Sizer
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-white leading-none">
            Set a target.<br />
            <span className="text-[#22C55E]">We size your positions.</span>
          </h1>
          <p className="mt-4 text-zinc-400 max-w-2xl">
            Tell us how much you want to win each week. We apply Accurate Monkey&apos;s 3-tier sliding scale (the same one our analysts use) to give you a personalized position ladder — locked in, then stepped down as you bank profit.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Input panel */}
          <section className="lg:col-span-2 bg-[#121212] border border-white/10 rounded-sm p-6" data-testid="optimizer-inputs">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-4">Your inputs</div>

            <label className="block mb-5">
              <div className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Target weekly profit (USD)</div>
              <div className="flex items-center gap-2 border border-white/15 focus-within:border-[#22C55E] rounded-sm bg-black/40 px-3 py-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="50"
                  value={target}
                  onChange={(e) => setTarget(Number(e.target.value) || 0)}
                  data-testid="optimizer-input-target"
                  className="flex-1 bg-transparent outline-none text-white font-display text-2xl"
                />
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">/ wk</span>
              </div>
              <div className="flex gap-2 mt-2">
                {[100, 250, 500, 1000, 2000, 5000].map((preset) => (
                  <button key={preset} onClick={() => setTarget(preset)}
                    data-testid={`optimizer-preset-${preset}`}
                    className={`text-[11px] px-2 py-1 rounded-sm border transition-colors ${
                      target === preset ? "border-[#22C55E] bg-[#22C55E]/15 text-[#22C55E]" : "border-white/15 text-zinc-400 hover:border-white/40"
                    }`}>
                    ${preset}
                  </button>
                ))}
              </div>
            </label>

            <label className="block mb-5">
              <div className="text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Bankroll <span className="text-zinc-600">(optional, for risk check)</span></div>
              <div className="flex items-center gap-2 border border-white/15 focus-within:border-[#22C55E] rounded-sm bg-black/40 px-3 py-2">
                <span className="text-zinc-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="500"
                  value={bankroll}
                  onChange={(e) => setBankroll(Number(e.target.value) || 0)}
                  data-testid="optimizer-input-bankroll"
                  className="flex-1 bg-transparent outline-none text-white font-display text-xl"
                />
              </div>
              <p className="text-[10px] text-zinc-600 mt-1">Recommended: keep base unit ≤ 5% of bankroll.</p>
            </label>

            {/* Save / Subscribe CTA */}
            {isSubscriber ? (
              <div className="space-y-2">
                <button onClick={save} disabled={busy}
                  data-testid="optimizer-save-btn"
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold text-xs uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40">
                  <FloppyDisk size={14} weight="fill" /> {busy ? "Saving…" : "Save my stake ladder"}
                </button>
                {savedLadder && (
                  <button onClick={reset} disabled={busy} data-testid="optimizer-reset-btn"
                    className="w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 border border-white/15 hover:border-red-500/50 text-zinc-400 hover:text-red-400 text-[11px] uppercase tracking-widest rounded-sm transition-colors disabled:opacity-40">
                    <Trash size={12} /> Clear saved ladder
                  </button>
                )}
              </div>
            ) : (
              <Link to="/pricing" data-testid="optimizer-subscribe-cta"
                className="block w-full text-center px-4 py-3 bg-[#22C55E] hover:bg-[#16A34A] text-black font-bold text-xs uppercase tracking-widest rounded-sm transition-colors">
                <Lock size={12} weight="fill" className="inline mr-1.5 -mt-0.5" /> Subscribe to apply these stakes to today's picks
              </Link>
            )}
          </section>

          {/* Output / Ladder */}
          <section className="lg:col-span-3 space-y-3" data-testid="optimizer-output">
            {ladder ? (
              <>
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="bg-[#121212] border-2 border-[#22C55E]/40 rounded-sm overflow-hidden">
                  <div className="px-5 py-3 bg-[#22C55E]/10 flex items-center justify-between">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E] font-bold">Your personalized ladder</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">Target {fmt$(ladder.target_weekly)}/wk</div>
                  </div>
                  <div className="divide-y divide-white/5">
                    <LadderRow tier="Tier 1 · Base" condition="Weekly P&L below 67% of target" unit={ladder.base_unit} color="#22C55E" testid="ladder-tier1" />
                    <LadderRow tier="Tier 2 · Lock-in" condition={`When weekly P&L reaches ${fmt$(ladder.tier2_at)}`} unit={ladder.tier2_unit} color="#F59E0B" testid="ladder-tier2" />
                    <LadderRow tier="Tier 3 · Preserve" condition={`When weekly P&L reaches ${fmt$(ladder.tier3_at)}`} unit={ladder.tier3_unit} color="#A855F7" testid="ladder-tier3" />
                  </div>
                </motion.div>

                {ladder.warnings.length > 0 && (
                  <div className="border border-[#FF6B35]/40 bg-[#FF6B35]/5 rounded-sm p-4 space-y-1.5" data-testid="optimizer-warnings">
                    {ladder.warnings.map((w, i) => (
                      <div key={i} className="text-xs text-[#FFB088] flex items-start gap-2">
                        <Warning size={14} weight="fill" className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}

                {savedLadder && isSubscriber && (
                  <div className="border border-white/10 bg-black/40 rounded-sm p-4 flex items-start gap-2.5 text-xs text-zinc-400" data-testid="optimizer-saved-notice">
                    <CheckCircle size={16} weight="fill" className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                    <span>
                      Saved on {new Date(savedLadder.saved_at).toLocaleDateString()}. Every pick card on{" "}
                      <Link to="/sports/mlb" className="text-[#22C55E] underline">/sports/mlb</Link> now shows your personalized stake instead of the default $250.
                    </span>
                  </div>
                )}

                {!isSubscriber && (
                  <Link to="/pricing" data-testid="optimizer-bottom-cta"
                    className="block border border-[#22C55E]/30 bg-gradient-to-r from-[#22C55E]/10 to-transparent hover:from-[#22C55E]/20 rounded-sm p-5 group transition-colors">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold mb-1">Subscribers only →</div>
                        <div className="font-display text-lg leading-tight">Apply this ladder to live picks</div>
                        <div className="text-xs text-zinc-400 mt-1">Save your ladder once and every pick card will show your personalized stake. 7-day free trial.</div>
                      </div>
                      <ArrowRight size={20} weight="bold" className="text-[#22C55E] group-hover:translate-x-1 transition-transform flex-shrink-0" />
                    </div>
                  </Link>
                )}

                {/* Math footnote */}
                <details className="text-xs text-zinc-500" data-testid="optimizer-math-explainer">
                  <summary className="cursor-pointer hover:text-zinc-300 transition-colors uppercase tracking-widest text-[10px] py-2">How this is calculated ↓</summary>
                  <div className="mt-2 p-4 bg-[#0a0a0a] border border-white/5 rounded-sm space-y-2">
                    <p>The base ladder targets <span className="text-zinc-300">$1,200/wk</span> with units <span className="text-zinc-300">$250 / $150 / $75</span>. We scale linearly:</p>
                    <p className="font-mono-d text-[11px] text-zinc-300">unit = base × (your_target / 1200), rounded to nearest $5</p>
                    <p>Tier thresholds are <span className="text-zinc-300">67%</span> and <span className="text-zinc-300">100%</span> of your weekly target — so for a $500 target you'd switch to tier 2 once you hit <span className="text-zinc-300">$333.50</span> for the week, and tier 3 at <span className="text-zinc-300">$500</span>.</p>
                    <p className="text-zinc-600">Picks are ~70% hit rate on MLB. Past results don't guarantee future returns. Always bet within your means.</p>
                  </div>
                </details>
              </>
            ) : (
              <div className="border border-white/10 rounded-sm p-8 text-center text-zinc-500 text-sm">Enter a target weekly profit to see your ladder.</div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function LadderRow({ tier, condition, unit, color, testid }) {
  return (
    <div className="px-5 py-4 flex items-center justify-between gap-4" data-testid={testid}>
      <div className="flex items-center gap-3 min-w-0">
        <div className="w-2 h-10 rounded-sm flex-shrink-0" style={{ background: color }} />
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color }}>{tier}</div>
          <div className="text-xs text-zinc-400 truncate">{condition}</div>
        </div>
      </div>
      <div className="text-right flex-shrink-0">
        <div className="text-[9px] uppercase tracking-widest text-zinc-500">Risk per pick</div>
        <div className="font-display text-2xl text-white">${unit}</div>
      </div>
    </div>
  );
}
